import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Eye, TrendingUp, TreeDeciduous, CreditCard, AlertTriangle, CheckCircle, Clock, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionStatusCard = ({ subscription, onRenew }) => {
  if (!subscription || !subscription.has_subscription) {
    return (
      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-6" data-testid="subscription-status-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-yellow-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-[#0A0A0A] mb-1">No Active Subscription</h3>
            <p className="text-[#525252] text-sm mb-4">
              Purchase an NFC card to activate your digital profile. Your profile will be visible for 365 days from purchase.
            </p>
            <Button onClick={onRenew} className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold">
              Get Your Card
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { status, days_remaining, end_date, is_expired, admin_enabled, plan_type } = subscription;
  
  // Admin-enabled bypass
  if (admin_enabled) {
    return (
      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-6" data-testid="subscription-status-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
            <Shield className="w-6 h-6 text-purple-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-[#0A0A0A] mb-1">Admin Enabled Account</h3>
            <p className="text-[#525252] text-sm">
              Your account has been manually enabled by an administrator. Your profile is active regardless of subscription status.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Expired subscription
  if (is_expired) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6" data-testid="subscription-status-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-[#0A0A0A] mb-1">Subscription Expired</h3>
            <p className="text-[#525252] text-sm mb-2">
              Your subscription expired on {new Date(end_date).toLocaleDateString()}. Your profile is currently hidden from public view.
            </p>
            <p className="text-red-400 text-sm font-medium mb-4">
              Renew now to reactivate your digital business card!
            </p>
            <Button onClick={onRenew} className="bg-red-500 hover:bg-red-600 text-[#0A0A0A] font-semibold">
              Renew Subscription - ₹365/year
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Warning: expiring soon (less than 30 days)
  if (days_remaining !== null && days_remaining <= 30) {
    return (
      <div className="bg-[#B8935A]/15 border border-[#B8935A]/20 rounded-xl p-6" data-testid="subscription-status-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#B8935A]/20 flex items-center justify-center flex-shrink-0">
            <Clock className="w-6 h-6 text-[#B8935A]" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-[#0A0A0A] mb-1">Subscription Expiring Soon</h3>
            <p className="text-[#525252] text-sm mb-2">
              Your {plan_type} subscription expires in <span className="text-[#B8935A] font-semibold">{days_remaining} days</span> on {new Date(end_date).toLocaleDateString()}.
            </p>
            <p className="text-[#B8935A] text-sm mb-4">
              Renew early to avoid any interruption to your profile!
            </p>
            <Button onClick={onRenew} className="bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white font-semibold">
              Renew Now - ₹365/year
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Active subscription
  return (
    <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-6" data-testid="subscription-status-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
          <CheckCircle className="w-6 h-6 text-green-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-[#0A0A0A]">Active Subscription</h3>
            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded-full capitalize">
              {plan_type}
            </span>
          </div>
          <p className="text-[#525252] text-sm">
            Your profile is active and visible to the public. 
            {days_remaining !== null && (
              <span> Subscription renews in <span className="text-green-400 font-medium">{days_remaining} days</span>.</span>
            )}
          </p>
          {end_date && (
            <p className="text-xs text-[#a3a3a3] mt-2">
              Valid until {new Date(end_date).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const Analytics = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState({ total_views: 0, views_by_date: [], trees_saved: 0, subscription: null });
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/profiles/me/analytics`, {
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-[#B8935A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="analytics-dashboard">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold text-[#0A0A0A]">Analytics</h1>
        <p className="text-[#525252] mt-1">Track your profile performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6" data-testid="total-views-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-[#B8935A]/15 flex items-center justify-center">
              <Eye className="w-5 h-5 text-[#B8935A]" />
            </div>
            <span className="text-sm text-[#525252]">Total Views</span>
          </div>
          <p className="text-3xl font-bold text-[#0A0A0A]" data-testid="total-views-count">{analytics.total_views}</p>
        </div>

        <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6" data-testid="views-today-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <span className="text-sm text-[#525252]">Views Today</span>
          </div>
          <p className="text-3xl font-bold text-[#0A0A0A]" data-testid="views-today-count">
            {analytics.views_by_date.find(v => v.date === new Date().toISOString().split('T')[0])?.views || 0}
          </p>
        </div>

        <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6" data-testid="avg-views-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
              <BarChart className="w-5 h-5 text-blue-500" />
            </div>
            <span className="text-sm text-[#525252]">Avg. Daily Views</span>
          </div>
          <p className="text-3xl font-bold text-[#0A0A0A]" data-testid="avg-views-count">
            {analytics.views_by_date.length > 0 
              ? Math.round(analytics.total_views / analytics.views_by_date.length)
              : 0
            }
          </p>
        </div>

        <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6" data-testid="peak-day-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple-500" />
            </div>
            <span className="text-sm text-[#525252]">Peak Day</span>
          </div>
          <p className="text-3xl font-bold text-[#0A0A0A]" data-testid="peak-day-count">
            {analytics.views_by_date.length > 0
              ? Math.max(...analytics.views_by_date.map(v => v.views))
              : 0
            }
          </p>
        </div>

        <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6" data-testid="trees-saved-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-green-600/10 flex items-center justify-center">
              <TreeDeciduous className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-sm text-[#525252]">Trees Saved</span>
          </div>
          <p className="text-3xl font-bold text-[#0A0A0A]" data-testid="trees-saved-count">
            {analytics.trees_saved < 1 
              ? analytics.trees_saved.toFixed(4)
              : analytics.trees_saved.toFixed(2)
            }
          </p>
          <p className="text-xs text-[#525252] mt-1">~8,000 cards = 1 tree</p>
        </div>
      </div>

      {/* Subscription Status Card */}
      <div className="mb-8">
        <SubscriptionStatusCard subscription={analytics.subscription} onRenew={() => navigate('/checkout?type=subscription')} />
      </div>

      <ChartSection viewsData={analytics.views_by_date} />
    </div>
  );
};

// Extracted chart component with memoized styles
const ChartSection = ({ viewsData }) => {
  const tickStyle = useMemo(() => ({ fill: '#9CA3AF' }), []);
  const tooltipStyle = useMemo(() => ({ 
    backgroundColor: '#09090b', 
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px'
  }), []);
  const labelStyle = useMemo(() => ({ color: '#fff' }), []);

  if (viewsData.length === 0) {
    return (
      <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-6">Views Over Time</h2>
        <div className="text-center py-12" data-testid="no-analytics-message">
          <p className="text-[#525252]">No views yet. Share your profile to start tracking!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
      <h2 className="text-xl font-semibold text-[#0A0A0A] mb-6">Views Over Time</h2>
      <ResponsiveContainer width="100%" height={300} data-testid="analytics-chart">
        <BarChart data={viewsData}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis 
            dataKey="date" 
            stroke="#9CA3AF" 
            tick={tickStyle}
          />
          <YAxis 
            stroke="#9CA3AF" 
            tick={tickStyle}
          />
          <Tooltip 
            contentStyle={tooltipStyle}
            labelStyle={labelStyle}
          />
          <Bar dataKey="views" fill="#3B82F6" radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Analytics;
